import { Stack, Typography, TypographyVariant } from '@mui/material';
import { PaymentMethod } from '@stripe/stripe-js';
import { StripeCustomer } from '../../../models/Stripe';
import Label from '../../label';

const PaymentMethodOption = ({
  paymentMethod,
  customer,
  defaultPayment,
  dense,
  showSecondaryText = true,
  textVariant,
  secondaryTextVariant,
  secondaryText,
}: {
  paymentMethod: PaymentMethod;
  customer?: StripeCustomer;
  defaultPayment?: boolean;
  dense?: boolean;
  showSecondaryText?: boolean;
  textVariant?: TypographyVariant;
  secondaryTextVariant?: TypographyVariant;
  secondaryText?: string;
}) => {
  const today = new Date();
  const thisMonth = today.getMonth() + 1; // Months are 0-based in JavaScript
  const thisYear = today.getFullYear();

  const expiredCard =
    paymentMethod.type === 'card' &&
    paymentMethod.card?.exp_month &&
    paymentMethod.card?.exp_year &&
    (thisYear > paymentMethod.card.exp_year ||
      (thisYear === paymentMethod.card.exp_year && thisMonth > paymentMethod.card.exp_month));

  return (
    <>
      {paymentMethod.type === 'us_bank_account' && (
        <Stack
          direction={dense ? 'column' : 'row'}
          justifyContent={dense ? 'center' : 'space-between'}
          alignItems={dense ? 'flex-start' : 'center'}
          spacing={dense ? 0 : 2}
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography
              variant={textVariant ? textVariant : dense ? 'subtitle1' : 'h6'}
              textTransform="capitalize"
            >
              {paymentMethod.us_bank_account?.bank_name} •••• {paymentMethod.us_bank_account?.last4}
            </Typography>
            {defaultPayment && !dense && <Label color="primary">Default</Label>}
          </Stack>
          {showSecondaryText && (
            <Typography
              variant={secondaryTextVariant ? secondaryTextVariant : dense ? 'caption' : 'body2'}
              color="text.secondary"
              textTransform="capitalize"
            >
              {secondaryText || paymentMethod.us_bank_account?.account_type}
            </Typography>
          )}
        </Stack>
      )}

      {paymentMethod.type === 'card' && (
        <Stack
          direction={dense ? 'column' : 'row'}
          justifyContent={dense ? 'center' : 'space-between'}
          alignItems={dense ? 'flex-start' : 'center'}
          spacing={dense ? 0 : 2}
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography
              variant={textVariant ? textVariant : dense ? 'subtitle1' : 'h6'}
              textTransform="capitalize"
              whiteSpace="nowrap"
            >
              {paymentMethod.card?.brand} •••• {paymentMethod.card?.last4}
            </Typography>
            {defaultPayment && !dense && <Label color="primary">Default</Label>}
          </Stack>
          {showSecondaryText && (
            <Typography
              variant={secondaryTextVariant ? secondaryTextVariant : dense ? 'caption' : 'body2'}
              color={expiredCard ? 'error.main' : 'text.secondary'}
              textTransform="capitalize"
            >
              {secondaryText ||
                `expires ${paymentMethod.card?.exp_month}/${paymentMethod.card?.exp_year}`}
            </Typography>
          )}
        </Stack>
      )}

      {paymentMethod.type === 'link' && (
        <Stack direction="row" alignItems="center" spacing={dense ? 0 : 2} width="100%">
          <Typography variant={textVariant ? textVariant : dense ? 'subtitle1' : 'h6'}>
            {customer?.email}
          </Typography>
          {defaultPayment && !dense && <Label color="primary">Default</Label>}
        </Stack>
      )}
    </>
  );
};

export const mapPaymentToIcon = (paymentMethod: PaymentMethod) => {
  const { us_bank_account, card } = paymentMethod;
  if (us_bank_account) {
    return 'mdi:bank';
  }
  if (card) {
    switch (card.brand) {
      case 'diners':
        return 'simple-icons:dinersclub';
      case 'unionpay':
        return 'logos:unionpay';
      case 'amex':
        return 'formkit:amex';
      case 'discover':
        return 'formkit:discover';
      case 'jcb':
        return 'logos:jcb';
      case 'mastercard':
        return 'formkit:mastercard';
      case 'visa':
        return 'formkit:visa';
      default:
        return 'ion:card';
    }
  }
  return 'streamline:payment-10';
};

export default PaymentMethodOption;
